import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { useTimer } from './TimerContext';
import { useWebSocketContext } from './WebSocketContext';
import { useActiveMeeting } from '../hooks/useActiveMeeting';
import {
  disconnectSpotify,
  fetchCurrentlyPlaying,
  fetchSpotifyDevices,
  fetchSpotifyStatus,
  fetchTeamListeningActivity,
  normalizeSpotifyPlaylistUri,
  postListeningActivity,
  refreshSpotifyAccessToken,
  spotifyNext,
  spotifyPause,
  spotifyPlay,
  spotifyPrevious,
  spotifySeek,
  spotifySetVolume,
  transferSpotifyPlayback,
} from '../services/spotifyAuth';

const PREF_TIMER_SYNC = 'spotify_timer_sync_enabled';
const PREF_FOCUS_URI = 'spotify_focus_playlist_uri';
const PREF_MEETING_PAUSE = 'spotify_meeting_pause_enabled';
const PREF_MEETING_RESUME = 'spotify_auto_resume_after_meeting';

function readBoolPref(key, fallback = false) {
  if (typeof window === 'undefined') return fallback;
  const raw = localStorage.getItem(key);
  if (raw === null) return fallback;
  return raw === '1' || raw === 'true';
}

function writeBoolPref(key, value) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(key, value ? '1' : '0');
}

function readStringPref(key, fallback = '') {
  if (typeof window === 'undefined') return fallback;
  return localStorage.getItem(key) || fallback;
}

const SpotifyContext = createContext({
  connected: false,
  isPremium: false,
  product: null,
  displayName: null,
  loadingStatus: true,
  currentTrack: null,
  isPlaying: false,
  position: 0,
  duration: 0,
  volume: 0.5,
  deviceId: null,
  devices: [],
  accountError: null,
  peerActivity: {},
  expanded: false,
  setExpanded: () => {},
  timerSyncEnabled: false,
  setTimerSyncEnabled: () => {},
  meetingPauseEnabled: false,
  setMeetingPauseEnabled: () => {},
  autoResumeAfterMeeting: false,
  setAutoResumeAfterMeeting: () => {},
  focusPlaylistUri: '',
  setFocusPlaylistUri: () => {},
  connectPlayer: async () => {},
  refreshStatus: async () => {},
  applyConnectionStatus: () => {},
  disconnect: async () => {},
  togglePlay: async () => {},
  resumePlayback: async () => {},
  pausePlayback: async () => {},
  nextTrack: async () => {},
  previousTrack: async () => {},
  seek: async () => {},
  setVolume: async () => {},
  fetchDevices: async () => {},
  transferToDevice: async () => {},
  startFocusPlaylist: async () => {},
});

const SDK_SRC = 'https://sdk.scdn.co/spotify-player.js';

function loadSpotifySdk() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.Spotify?.Player) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SDK_SRC}"]`);
    if (existing) {
      if (window.Spotify?.Player) {
        resolve();
        return;
      }
      const prev = window.onSpotifyWebPlaybackSDKReady;
      window.onSpotifyWebPlaybackSDKReady = () => {
        if (typeof prev === 'function') prev();
        resolve();
      };
      return;
    }

    window.onSpotifyWebPlaybackSDKReady = () => resolve();
    const script = document.createElement('script');
    script.src = SDK_SRC;
    script.async = true;
    script.onerror = () => reject(new Error('Failed to load Spotify Web Playback SDK'));
    document.body.appendChild(script);
  });
}

export function SpotifyProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { subscribe, sendMessage } = useWebSocketContext();
  const { activeTimer } = useTimer();

  const [connected, setConnected] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [product, setProduct] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.5);
  const [deviceId, setDeviceId] = useState(null);
  const [devices, setDevices] = useState([]);
  const [accountError, setAccountError] = useState(null);
  const [peerActivity, setPeerActivity] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [timerSyncEnabled, setTimerSyncEnabledState] = useState(() =>
    readBoolPref(PREF_TIMER_SYNC, false)
  );
  const [meetingPauseEnabled, setMeetingPauseEnabledState] = useState(() =>
    readBoolPref(PREF_MEETING_PAUSE, false)
  );
  const [autoResumeAfterMeeting, setAutoResumeAfterMeetingState] = useState(() =>
    readBoolPref(PREF_MEETING_RESUME, false)
  );
  const [focusPlaylistUri, setFocusPlaylistUriState] = useState(() =>
    readStringPref(PREF_FOCUS_URI, '')
  );

  const playerRef = useRef(null);
  const lastActivityKeyRef = useRef('');
  const pollRef = useRef(null);
  const statusRequestIdRef = useRef(0);
  const isPlayingRef = useRef(false);
  const deviceIdRef = useRef(null);
  const prevTimerRef = useRef(undefined);
  const prevMeetingActiveRef = useRef(undefined);
  const pausedForMeetingRef = useRef(false);

  const { isMeetingActive, calendarConnected } = useActiveMeeting({
    enabled: isAuthenticated && meetingPauseEnabled,
  });

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    deviceIdRef.current = deviceId;
  }, [deviceId]);

  const applyConnectionStatus = useCallback((status) => {
    // Invalidate in-flight status fetches so a stale "not connected" can't win the race.
    statusRequestIdRef.current += 1;
    setConnected(Boolean(status?.connected));
    setIsPremium(Boolean(status?.is_premium));
    setProduct(status?.product || null);
    setDisplayName(status?.display_name || null);
    setAccountError(null);
    setLoadingStatus(false);
  }, []);

  const refreshStatus = useCallback(async () => {
    const hasToken =
      typeof window !== 'undefined' && Boolean(localStorage.getItem('token'));
    if (!isAuthenticated && !hasToken) {
      statusRequestIdRef.current += 1;
      setConnected(false);
      setLoadingStatus(false);
      return null;
    }

    const requestId = ++statusRequestIdRef.current;
    setLoadingStatus(true);
    try {
      const status = await fetchSpotifyStatus();
      if (requestId !== statusRequestIdRef.current) return status;
      setConnected(Boolean(status?.connected));
      setIsPremium(Boolean(status?.is_premium));
      setProduct(status?.product || null);
      setDisplayName(status?.display_name || null);
      return status;
    } catch (_) {
      // Don't clear a just-applied connection on a transient status fetch failure.
      if (requestId !== statusRequestIdRef.current) return null;
      return null;
    } finally {
      if (requestId === statusRequestIdRef.current) {
        setLoadingStatus(false);
      }
    }
  }, [isAuthenticated]);

  const broadcastActivity = useCallback(async (track, playing) => {
    const key = `${playing ? '1' : '0'}:${track?.uri || track?.name || 'none'}`;
    if (key === lastActivityKeyRef.current) return;
    lastActivityKeyRef.current = key;

    const payload = playing && track
      ? {
          track_name: track.name,
          artist: track.artist,
          album_art_url: track.albumArt,
          is_playing: true,
          spotify_track_uri: track.uri,
        }
      : { is_playing: false };

    try {
      await postListeningActivity(payload);
    } catch (_) {
      /* ignore presence failures */
    }

    // Best-effort local WS fan-out if chat socket accepts custom types
    try {
      sendMessage?.({
        type: playing ? 'spotify:now_playing' : 'spotify:stopped',
        data: playing
          ? {
              track_name: track?.name,
              artist: track?.artist,
              album_art_url: track?.albumArt,
              is_playing: true,
            }
          : {},
      });
    } catch (_) {
      /* ignore */
    }
  }, [sendMessage]);

  const applyPlayerState = useCallback(
    (state) => {
      if (!state) {
        setIsPlaying(false);
        setCurrentTrack(null);
        setPosition(0);
        setDuration(0);
        broadcastActivity(null, false);
        return;
      }
      const track = state.track_window?.current_track;
      const mapped = track
        ? {
            id: track.id,
            name: track.name,
            artist: (track.artists || []).map((a) => a.name).join(', '),
            album: track.album?.name,
            albumArt: track.album?.images?.[0]?.url || null,
            uri: track.uri,
            durationMs: track.duration_ms,
          }
        : null;
      setCurrentTrack(mapped);
      setIsPlaying(!state.paused);
      setPosition(state.position || 0);
      setDuration(track?.duration_ms || 0);
      broadcastActivity(mapped, !state.paused);
    },
    [broadcastActivity]
  );

  const connectPlayer = useCallback(async () => {
    if (!connected || !isPremium) return;
    setAccountError(null);

    try {
      await loadSpotifySdk();
      if (playerRef.current) {
        await playerRef.current.connect();
        return;
      }

      const player = new window.Spotify.Player({
        name: 'Tick Sync Player',
        getOAuthToken: async (cb) => {
          try {
            const data = await refreshSpotifyAccessToken();
            cb(data.access_token);
          } catch (err) {
            setAccountError('Spotify session expired. Reconnect in Settings.');
            cb('');
          }
        },
        volume,
      });

      player.addListener('ready', ({ device_id }) => {
        setDeviceId(device_id);
      });
      player.addListener('not_ready', () => {
        setDeviceId(null);
      });
      player.addListener('player_state_changed', applyPlayerState);
      player.addListener('authentication_error', ({ message }) => {
        setAccountError(message || 'Spotify authentication failed');
      });
      player.addListener('account_error', ({ message }) => {
        setAccountError(
          message ||
            'Spotify Premium is required to play music in Tick Sync. Upgrade to Premium to enable in-app playback.'
        );
        setIsPremium(false);
      });
      player.addListener('initialization_error', ({ message }) => {
        setAccountError(
          message
            ? `${message}. Controls still work through your Spotify app.`
            : 'In-browser player unavailable. Controls still work through your Spotify app.'
        );
      });

      const ok = await player.connect();
      if (!ok) {
        setAccountError('Could not connect Spotify player');
      }
      playerRef.current = player;
    } catch (err) {
      setAccountError(err?.message || 'Failed to start Spotify player');
    }
  }, [applyPlayerState, connected, isPremium, volume]);

  const disconnectPlayer = useCallback(() => {
    if (playerRef.current) {
      try {
        playerRef.current.disconnect();
      } catch (_) {
        /* ignore */
      }
      playerRef.current = null;
    }
    setDeviceId(null);
    setCurrentTrack(null);
    setIsPlaying(false);
  }, []);

  const disconnect = useCallback(async () => {
    await disconnectSpotify();
    disconnectPlayer();
    setConnected(false);
    setIsPremium(false);
    setProduct(null);
    setDisplayName(null);
    setAccountError(null);
    await broadcastActivity(null, false);
  }, [broadcastActivity, disconnectPlayer]);

  const applyCurrentlyPlaying = useCallback(
    (data) => {
      if (data?.track) {
        const mapped = {
          id: data.track.id,
          name: data.track.name,
          artist: data.track.artist,
          album: data.track.album,
          albumArt: data.track.album_art_url,
          uri: data.track.uri,
          durationMs: data.track.duration_ms,
        };
        setCurrentTrack(mapped);
        setIsPlaying(Boolean(data.is_playing));
        setPosition(data.progress_ms || 0);
        setDuration(data.track.duration_ms || 0);
        broadcastActivity(mapped, Boolean(data.is_playing));
        return;
      }
      setIsPlaying(false);
      broadcastActivity(null, false);
    },
    [broadcastActivity]
  );

  const syncCurrentlyPlaying = useCallback(async () => {
    try {
      const data = await fetchCurrentlyPlaying();
      applyCurrentlyPlaying(data);
      return data;
    } catch (_) {
      return null;
    }
  }, [applyCurrentlyPlaying]);

  const refreshAfterControl = useCallback(() => {
    // Don't block the button on sync — Spotify needs a short beat to settle metadata.
    const delayMs = process.env.NODE_ENV === 'test' ? 0 : 120;
    const run = async () => {
      if (delayMs) {
        await new Promise((r) => setTimeout(r, delayMs));
      }
      await syncCurrentlyPlaying();
    };
    return run();
  }, [syncCurrentlyPlaying]);

  const handlePlaybackError = useCallback(
    (err, revertPlayingTo) => {
      if (typeof revertPlayingTo === 'boolean') {
        setIsPlaying(revertPlayingTo);
      }
      const detail =
        err?.response?.data?.detail || err?.message || 'Playback control failed';
      if (String(detail).toLowerCase().includes('restriction violated')) {
        setAccountError(null);
        syncCurrentlyPlaying();
        return;
      }
      setAccountError(detail);
      syncCurrentlyPlaying();
    },
    [syncCurrentlyPlaying]
  );

  const resumePlayback = useCallback(async () => {
    if (!connected || !isPremium) return;
    const wasPlaying = isPlayingRef.current;
    setIsPlaying(true);
    setAccountError(null);
    try {
      await spotifyPlay();
      refreshAfterControl();
    } catch (err) {
      handlePlaybackError(err, wasPlaying);
    }
  }, [connected, handlePlaybackError, isPremium, refreshAfterControl]);

  const pausePlayback = useCallback(async () => {
    if (!connected || !isPremium) return;
    const wasPlaying = isPlayingRef.current;
    setIsPlaying(false);
    setAccountError(null);
    try {
      await spotifyPause();
      refreshAfterControl();
    } catch (err) {
      handlePlaybackError(err, wasPlaying);
    }
  }, [connected, handlePlaybackError, isPremium, refreshAfterControl]);

  // Always remote-control via Spotify Web API (desktop/phone). The Web Playback SDK
  // often registers a ghost "Tick Sync Player" that swallows commands with no audio.
  const togglePlay = useCallback(async () => {
    if (isPlayingRef.current) {
      await pausePlayback();
    } else {
      await resumePlayback();
    }
  }, [pausePlayback, resumePlayback]);

  const setTimerSyncEnabled = useCallback((value) => {
    const next = Boolean(value);
    setTimerSyncEnabledState(next);
    writeBoolPref(PREF_TIMER_SYNC, next);
  }, []);

  const setMeetingPauseEnabled = useCallback((value) => {
    const next = Boolean(value);
    setMeetingPauseEnabledState(next);
    writeBoolPref(PREF_MEETING_PAUSE, next);
  }, []);

  const setAutoResumeAfterMeeting = useCallback((value) => {
    const next = Boolean(value);
    setAutoResumeAfterMeetingState(next);
    writeBoolPref(PREF_MEETING_RESUME, next);
  }, []);

  const setFocusPlaylistUri = useCallback((value) => {
    const normalized = normalizeSpotifyPlaylistUri(value);
    setFocusPlaylistUriState(normalized);
    if (typeof window !== 'undefined') {
      if (normalized) localStorage.setItem(PREF_FOCUS_URI, normalized);
      else localStorage.removeItem(PREF_FOCUS_URI);
    }
  }, []);

  const fetchDevices = useCallback(async () => {
    if (!connected) {
      setDevices([]);
      return [];
    }
    try {
      const list = await fetchSpotifyDevices();
      setDevices(list);
      return list;
    } catch (err) {
      setAccountError(
        err?.response?.data?.detail || err?.message || 'Could not load Spotify devices'
      );
      return [];
    }
  }, [connected]);

  const transferToDevice = useCallback(
    async (targetDeviceId, { play = false } = {}) => {
      if (!targetDeviceId) return;
      setAccountError(null);
      try {
        await transferSpotifyPlayback(targetDeviceId, { play });
        setDevices((prev) =>
          prev.map((d) => ({
            ...d,
            is_active: d.id === targetDeviceId,
          }))
        );
        refreshAfterControl();
      } catch (err) {
        setAccountError(
          err?.response?.data?.detail || err?.message || 'Could not switch Spotify device'
        );
      }
    },
    [refreshAfterControl]
  );

  const startFocusPlaylist = useCallback(async () => {
    const uri = normalizeSpotifyPlaylistUri(focusPlaylistUri);
    if (!uri) {
      setAccountError('Set a focus playlist URI in Spotify settings first.');
      return;
    }
    if (!connected || !isPremium) return;
    setAccountError(null);
    setIsPlaying(true);
    try {
      await spotifyPlay({ context_uri: uri });
      refreshAfterControl();
    } catch (err) {
      handlePlaybackError(err, isPlayingRef.current);
    }
  }, [connected, focusPlaylistUri, handlePlaybackError, isPremium, refreshAfterControl]);

  const nextTrack = useCallback(async () => {
    setAccountError(null);
    try {
      await spotifyNext();
      refreshAfterControl();
    } catch (err) {
      setAccountError(
        err?.response?.data?.detail || err?.message || 'Could not skip to next track'
      );
      syncCurrentlyPlaying();
    }
  }, [refreshAfterControl, syncCurrentlyPlaying]);

  const previousTrack = useCallback(async () => {
    setAccountError(null);
    try {
      await spotifyPrevious();
      refreshAfterControl();
    } catch (err) {
      setAccountError(
        err?.response?.data?.detail ||
          err?.message ||
          'Could not skip to previous track'
      );
      syncCurrentlyPlaying();
    }
  }, [refreshAfterControl, syncCurrentlyPlaying]);

  const seek = useCallback(
    async (ms) => {
      const positionMs = Math.max(0, Math.floor(Number(ms) || 0));
      setPosition(positionMs);
      setAccountError(null);
      try {
        await spotifySeek(positionMs);
        refreshAfterControl();
      } catch (err) {
        setAccountError(err?.response?.data?.detail || err?.message || 'Seek failed');
        syncCurrentlyPlaying();
      }
    },
    [refreshAfterControl, syncCurrentlyPlaying]
  );

  const setVolume = useCallback(async (value) => {
    const v = Math.max(0, Math.min(1, Number(value) || 0));
    setVolumeState(v);
    try {
      await spotifySetVolume(Math.round(v * 100));
      setAccountError(null);
    } catch (err) {
      setAccountError(
        err?.response?.data?.detail || err?.message || 'Volume change failed'
      );
    }
  }, []);

  // Load connection status when authenticated
  useEffect(() => {
    const hasToken =
      typeof window !== 'undefined' && Boolean(localStorage.getItem('token'));

    // During auth bootstrap (token present, isAuthenticated still false), don't wipe
    // a just-applied OAuth connection — wait for auth to settle.
    if (!isAuthenticated) {
      if (!hasToken) {
        disconnectPlayer();
        setConnected(false);
        setLoadingStatus(false);
        setPeerActivity({});
      }
      return undefined;
    }
    refreshStatus();
    fetchTeamListeningActivity()
      .then((activities) => {
        const map = {};
        (activities || []).forEach((a) => {
          if (a.user_id) map[a.user_id] = a;
        });
        setPeerActivity(map);
      })
      .catch(() => {});
    return undefined;
  }, [disconnectPlayer, isAuthenticated, refreshStatus]);

  // Do not auto-connect the Web Playback SDK — it registers "Tick Sync Player" and
  // often steals the active device so play/pause/next appear to do nothing.
  // Tear down any leftover SDK player when Spotify disconnects.
  useEffect(() => {
    if (!connected) {
      disconnectPlayer();
    }
  }, [connected, disconnectPlayer]);

  useEffect(() => {
    return () => {
      disconnectPlayer();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [disconnectPlayer]);

  // Poll currently-playing so the header tracks the user's real Spotify app.
  useEffect(() => {
    if (!connected) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }

    const poll = async () => {
      await syncCurrentlyPlaying();
    };

    poll();
    const intervalMs = isPlaying ? 5000 : 15000;
    pollRef.current = setInterval(poll, intervalMs);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [connected, isPlaying, syncCurrentlyPlaying]);

  // Progress ticker while playing
  useEffect(() => {
    if (!isPlaying) return undefined;
    const id = setInterval(() => {
      setPosition((p) => {
        if (!duration) return p;
        return Math.min(p + 1000, duration);
      });
    }, 1000);
    return () => clearInterval(id);
  }, [duration, isPlaying]);

  // Peer listening activity via WebSocket
  useEffect(() => {
    if (!subscribe) return undefined;
    return subscribe((payload) => {
      const type = payload?.type;
      const data = payload?.data || {};
      if (type === 'spotify:now_playing' && data.user_id) {
        setPeerActivity((prev) => ({
          ...prev,
          [data.user_id]: {
            user_id: data.user_id,
            track_name: data.track_name,
            artist: data.artist,
            album_art_url: data.album_art_url,
            is_playing: true,
          },
        }));
      }
      if (type === 'spotify:stopped' && data.user_id) {
        setPeerActivity((prev) => {
          const next = { ...prev };
          delete next[data.user_id];
          return next;
        });
      }
    });
  }, [subscribe]);

  // Timer ↔ Spotify sync (opt-in). Skip initial hydrate so a running timer on
  // page load does not force a resume.
  useEffect(() => {
    if (!timerSyncEnabled || !connected || !isPremium) {
      prevTimerRef.current = activeTimer;
      return;
    }
    if (prevTimerRef.current === undefined) {
      prevTimerRef.current = activeTimer;
      return;
    }
    const wasRunning = Boolean(prevTimerRef.current);
    const isRunning = Boolean(activeTimer);
    prevTimerRef.current = activeTimer;
    if (!wasRunning && isRunning) {
      resumePlayback();
    } else if (wasRunning && !isRunning) {
      pausePlayback();
    }
  }, [
    activeTimer,
    connected,
    isPremium,
    pausePlayback,
    resumePlayback,
    timerSyncEnabled,
  ]);

  // Auto-pause when an Outlook meeting becomes active (opt-in).
  useEffect(() => {
    if (!meetingPauseEnabled || !connected || !isPremium || !calendarConnected) {
      prevMeetingActiveRef.current = isMeetingActive;
      return;
    }
    if (prevMeetingActiveRef.current === undefined) {
      prevMeetingActiveRef.current = isMeetingActive;
      if (isMeetingActive && isPlayingRef.current) {
        pausedForMeetingRef.current = true;
        pausePlayback();
      }
      return;
    }
    const wasActive = Boolean(prevMeetingActiveRef.current);
    prevMeetingActiveRef.current = isMeetingActive;
    if (!wasActive && isMeetingActive) {
      if (isPlayingRef.current) {
        pausedForMeetingRef.current = true;
        pausePlayback();
      }
    } else if (wasActive && !isMeetingActive) {
      if (autoResumeAfterMeeting && pausedForMeetingRef.current) {
        pausedForMeetingRef.current = false;
        resumePlayback();
      } else {
        pausedForMeetingRef.current = false;
      }
    }
  }, [
    autoResumeAfterMeeting,
    calendarConnected,
    connected,
    isMeetingActive,
    isPremium,
    meetingPauseEnabled,
    pausePlayback,
    resumePlayback,
  ]);

  // Refresh device list when the expanded player opens.
  useEffect(() => {
    if (expanded && connected && isPremium) {
      fetchDevices();
    }
  }, [connected, expanded, fetchDevices, isPremium]);

  const value = useMemo(
    () => ({
      connected,
      isPremium,
      product,
      displayName,
      loadingStatus,
      currentTrack,
      isPlaying,
      position,
      duration,
      volume,
      deviceId,
      devices,
      accountError,
      peerActivity,
      expanded,
      setExpanded,
      timerSyncEnabled,
      setTimerSyncEnabled,
      meetingPauseEnabled,
      setMeetingPauseEnabled,
      autoResumeAfterMeeting,
      setAutoResumeAfterMeeting,
      focusPlaylistUri,
      setFocusPlaylistUri,
      connectPlayer,
      refreshStatus,
      applyConnectionStatus,
      disconnect,
      togglePlay,
      resumePlayback,
      pausePlayback,
      nextTrack,
      previousTrack,
      seek,
      setVolume,
      fetchDevices,
      transferToDevice,
      startFocusPlaylist,
      calendarConnected,
    }),
    [
      accountError,
      applyConnectionStatus,
      autoResumeAfterMeeting,
      calendarConnected,
      connectPlayer,
      connected,
      currentTrack,
      deviceId,
      devices,
      disconnect,
      displayName,
      duration,
      expanded,
      fetchDevices,
      focusPlaylistUri,
      isPlaying,
      isPremium,
      loadingStatus,
      meetingPauseEnabled,
      nextTrack,
      pausePlayback,
      peerActivity,
      position,
      previousTrack,
      product,
      refreshStatus,
      resumePlayback,
      seek,
      setAutoResumeAfterMeeting,
      setFocusPlaylistUri,
      setMeetingPauseEnabled,
      setTimerSyncEnabled,
      setVolume,
      startFocusPlaylist,
      timerSyncEnabled,
      togglePlay,
      transferToDevice,
      volume,
    ]
  );

  return <SpotifyContext.Provider value={value}>{children}</SpotifyContext.Provider>;
}

export function useSpotify() {
  return useContext(SpotifyContext);
}

export default SpotifyContext;
